import type { NotUndefined } from '@redux-saga/types';
import type { EventChannel } from 'redux-saga';
import { take } from 'redux-saga/effects';

/**
 * Take from an EventChannel until a predicate matches.
 *
 * Useful when multiple producers share a channel (e.g. a poller + a request/response),
 * so unrelated in-flight events cannot "steal" the next `take()`.
 */
export function* takeUntil<T extends NotUndefined>(
  channel: EventChannel<T>,
  predicate: (event: T) => boolean,
): Generator<unknown, T, T> {
  while (true) {
    const event: T = (yield take(channel)) as T;
    if (predicate(event)) return event;
  }
}

/**
 * Take from an EventChannel until a predicate matches, while preserving non-matching events.
 *
 * Unlike {@link takeUntil}, this does *not* discard non-matching events: it buffers them and returns
 * them to the caller so another consumer can process them later.
 */
export function* takeUntilPreserving<T extends NotUndefined>(
  channel: EventChannel<T>,
  predicate: (event: T) => boolean,
): Generator<unknown, { matched: T; buffered: T[] }, T> {
  const buffered: T[] = [];
  while (true) {
    const event: T = (yield take(channel)) as T;
    if (predicate(event)) return { matched: event, buffered };
    buffered.push(event);
  }
}
